<template>
	<v-card
		variant="outlined"
		class="resource-card-shell"
		min-width="300px"
		data-test="build-card"
	>
		<div
			v-if="buildData"
			class="resource-card"
		>
			<!-- Header: Build ID, Core, Public -->
			<div class="resource-card__header sgnl-paragraph-s-regular">
				<div class="resource-card__header-info">
					<RepoSourceCodeReference20
						v-if="buildData.Application?.Type === 'Repository'"
						class="sgnl-flex-shrink-0 sgnl-icon-color-secondary"
					/>
					<CodeReference20
						v-else-if="buildData.Application?.Type === 'SourceCode'"
						class="sgnl-flex-shrink-0 sgnl-icon-color-secondary"
					/>
					<v-icon
						v-else
						class="sgnl-pr-4 source-icon"
					>
						{{ projectSourceIcon }}
					</v-icon>
					<v-chip
						v-if="buildData.BuildID != null"
						variant="outlined"
						class="badge-muted copy-id-chip"
						data-test="build-card-build-id-value"
					>
						Build ID: <span class="truncated">{{ util.shortenID(buildData.BuildID) }}</span>
						<CopyToClipboardButton
							:value-to-copy="buildData.BuildID"
							value-name="Build ID"
						/>
					</v-chip>
					<v-chip
						v-if="buildData.IsPublic"
						variant="outlined"
						data-test="build-card-public-pill"
					>
						<Earth16 />
						Public
					</v-chip>
					<template v-if="buildCoreSpec && !$vuetify.display.mobile">
						<span class="sgnl-text-color-secondary">on</span>
						<v-tooltip
							open-delay="500"
							content-class="tooltip-transparent"
						>
							<template v-slot:activator="{ props }">
								<v-chip
									v-bind="props"
									variant="outlined"
									class="sgnl-font-weight-medium"
								>
									<Chip16 />
									{{ buildCoreName }}
								</v-chip>
							</template>
							<ActiveCoreTooltip :active-core="buildCoreSpec" />
						</v-tooltip>
					</template>
				</div>
				<div class="resource-card__status status-label sgnl-text-color-secondary">
					<span :class="['status-dot', statusDotClass]" />
					<span>{{ buildData.Status }}</span>
				</div>
			</div>
			<div class="build-card-body resource-card__description">
				<SourceReference
					:repo="cardRepo"
					:source-code="cardSourceCode"
					:show-build-directory="buildData.BuildID != null"
				/>
				<!-- Error message -->
				<code
					v-if="buildStatusMessageOnError"
					class="build-error-message sgnl-inline-flex sgnl-align-center sgnl-text-color-error sgnl-gap-8"
				>
					<WarningAlt16 class="build-error-icon sgnl-flex-shrink-0" />
					{{ buildStatusMessageOnError }}
				</code>
				<!-- Info badges -->
				<div class="sgnl-flex sgnl-flex-wrap info-badges">
					<!-- Mobile core -->
					<template v-if="$vuetify.display.mobile && buildCoreSpec">
						<v-tooltip
							open-delay="500"
							content-class="tooltip-transparent"
						>
							<template v-slot:activator="{ props }">
								<v-chip
									v-bind="props"
									variant="text"
									class="sgnl-font-weight-medium"
								>
									<Chip16 />
									{{ buildCoreName }}
								</v-chip>
							</template>
							<ActiveCoreTooltip :active-core="buildCoreSpec" />
						</v-tooltip>
					</template>
					<!-- Duration -->
					<v-chip variant="text">
						<Timer16 />
						<template v-if="isBuildActive(buildData.Status)"> Build in progress </template>
						<template v-else>
							{{ buildDuration }}
						</template>
					</v-chip>
					<!-- Timestamp -->
					<v-chip variant="text">
						<Calendar16 />
						<template v-if="buildData.Status === BuildStatusE.Accepted || !buildData.StartedAt">
							Queued {{ createdTimestamp }}
						</template>
						<template v-else>{{ startTimestamp }}</template>
						<template v-if="!isBuildActive(buildData.Status) && buildData.FinishedAt">
							<ArrowRight16 />
							{{ endTimestamp }}
						</template>
					</v-chip>
					<!-- Active build progress -->
					<v-progress-circular
						v-if="isBuildActive(buildData.Status)"
						indeterminate
						size="16"
						width="2"
						:color="util.buildStatusColor(buildData?.Status)"
					/>
				</div>
				<!-- Compilation and link flags -->
				<div
					v-if="buildConfigMk"
					class="sgnl-flex sgnl-align-center sgnl-gap-8"
				>
					<Tools16 class="sgnl-icon-color-secondary" />
					<div
						code-area
						textarea-narrowLineHeight
						class="argument-area"
					>
						<span>{{ buildConfigMk }}</span>
					</div>
				</div>
			</div>

			<!-- Actions Section -->
			<div
				v-if="isBuildActive(buildData.Status)"
				class="sgnl-flex sgnl-align-center sgnl-flex-wrap sgnl-gap-8"
			>
				<v-spacer />
				<v-btn
					variant="outlined"
					color="error"
					size="small"
					:loading="cancel.isCancellingBuild(buildData?.BuildID)"
					v-on:click="cancelActiveBuild"
					data-test="build-card-cancel-button"
				>
					Cancel Build
				</v-btn>
			</div>
			<div
				v-if="buildData.Status === BuildStatusE.Completed"
				class="sgnl-flex sgnl-align-center sgnl-flex-wrap sgnl-gap-8"
			>
				<TooltipButton
					variant="flat"
					color="primary"
					min-width="0px"
					:loading="activeTask?.Status ? isTaskActive(activeTask.Status) : false"
					@click="runTask()"
					tooltipText="Create and run a task from this build"
					data-test="build-card-run-task-button"
				>
					<template #btnContent>
						<PlayFilledAlt16 />
						<span>Run Task</span>
					</template>
				</TooltipButton>
				<TooltipButton
					v-if="activeTask?.TaskID"
					variant="outlined"
					min-width="0px"
					@click="openOutputsDialog()"
					tooltipText="View build outputs"
					data-test="build-card-view-outputs-button"
				>
					<template #btnContent>
						<Document16 />
						<span>View Outputs</span>
					</template>
				</TooltipButton>
				<div class="build-card-inline-actions">
					<v-tooltip>
						<template v-slot:activator="{ props }">
							<v-btn
								variant="outlined"
								@click="openRunArgumentsDialog"
								data-test="build-run-argument-selector-chip"
								v-bind="props"
							>
								<Terminal16 />
							</v-btn>
						</template>
						Command-Line Arguments
					</v-tooltip>
					<v-tooltip>
						<template v-slot:activator="{ props }">
							<v-btn
								variant="outlined"
								@click="openMountDataSourcesDialog"
								data-test="build-data-source-selector-chip"
								v-bind="props"
							>
								<DataCheck16 />
							</v-btn>
						</template>
						Mount Data Source
					</v-tooltip>
				</div>
				<v-spacer />
				<v-tooltip>
					<template v-slot:activator="{ props }">
						<v-btn
							variant="outlined"
							color="error"
							@click="openRemoveBuildDialog()"
							data-test="build-card-open-remove-button"
							v-bind="props"
						>
							<TrashCan16 />
						</v-btn>
					</template>
					Delete build
				</v-tooltip>
			</div>
			<div
				v-else-if="
					buildData.Status === BuildStatusE.Error ||
					buildData.Status === BuildStatusE.Stopped ||
					buildData.Status === BuildStatusE.Cancelled
				"
				class="sgnl-flex sgnl-align-center sgnl-flex-wrap sgnl-gap-8"
			>
				<TooltipButton
					variant="outlined"
					min-width="0px"
					@click="openOutputsDialog()"
					tooltipText="View build outputs"
					data-test="build-card-view-outputs-button"
				>
					<template #btnContent>
						<Document16 />
						<span>View Outputs</span>
					</template>
				</TooltipButton>
				<v-spacer />
				<v-tooltip>
					<template v-slot:activator="{ props }">
						<v-btn
							variant="outlined"
							color="error"
							@click="openRemoveBuildDialog()"
							data-test="build-card-open-remove-button"
							v-bind="props"
						>
							<TrashCan16 />
						</v-btn>
					</template>
					Delete build
				</v-tooltip>
			</div>

			<!-- Build Bar for task progress -->
			<div
				v-if="showBuildBar"
				class="resource-card__build-bar"
			>
				<v-expand-transition>
					<BuildBar
						v-show="showBuildBar"
						:taskID="activeTask?.TaskID"
						:taskStatus="activeTask?.Status"
						:taskAccessState="mergedTaskAccessState"
						:activeTask="activeTask"
						:hide-build-section="true"
						:showViewTask="true"
						@cancel-task="cancelActiveTask"
						@retry-task-output="retryTaskOutput"
					/>
				</v-expand-transition>
			</div>

			<ExecutionResultsCard
				v-model:is-visible="outputsDialog"
				:task-i-d="activeTask?.TaskID"
				:build-i-d="buildData.BuildID"
				@click-outside="closeOutputsDialog"
			/>
			<BuildRemoveDialog
				:show-dialog="removeBuildDialog"
				:build-i-d="buildID"
				@close-remove-build-dialog="closeRemoveBuildDialog"
				@remove-build-success="removeBuildSuccess"
			/>
			<RunArgumentsDialog
				v-if="showRunArgumentsDialog"
				:showDialog="showRunArgumentsDialog"
				:initialRunArguments="runArgumentsBuffer"
				@close-dialog="closeRunArgumentsDialog"
				@set-arguments="setRunArguments"
			/>
			<MountDataSourcesDialog
				v-if="showMountDataSourcesDialog"
				:showDialog="showMountDataSourcesDialog"
				:initialMountConfig="currentMountConfig"
				@close-dialog="closeMountDataSourcesDialog"
				@set-data-sources="setMountConfig"
				@reset-data-sources="setMountConfig"
				@remove-data-sources="setMountConfig"
			/>
		</div>
	</v-card>
</template>

<script lang="ts">
import { PropType, defineComponent, provide } from "vue";
import { useCancelWithFeedback, CancelFeedbackKey } from "@/composables/useCancelWithFeedback";

// Types
import { BuildStatus, BuildStatusE, Build, isBuildActive, isBuildFail, isBuildTerminated } from "@/types/api/builds";
import { LocalMountConfig, DataSource } from "@/types/api/dataSources";
import { Task, TaskStatusE, isTaskActive, isTaskTerminated } from "@/types/api/tasks";
import { RemoteAccessState } from "@/types/general";

// Libraries
import moment from "moment";

// Utilities
import { sdk } from "@/js/sdk";
import * as util from "@/js/utilities";
import { monitoringCaptureError } from "@/plugins/monitoring";

// Components
import TooltipButton from "@/components/Common/TooltipButton.vue";
import CopyToClipboardButton from "@/components/Common/CopyToClipboardButton.vue";
import ActiveCoreTooltip from "@/components/Repositories/ActiveCoreTooltip.vue";
import BuildBar from "@/components/Common/BuildBar.vue";
import ExecutionResultsCard from "@/components/Repositories/ExecutionResultsCard.vue";
import BuildRemoveDialog from "@/components/TaskManager/BuildRemoveDialog.vue";
import RunArgumentsDialog from "@/components/RunArgumentsDialog.vue";
import MountDataSourcesDialog from "@/components/MountDataSourcesDialog.vue";
import SourceReference from "@/components/TaskManager/SourceReference.vue";

// Carbon icons
import RepoSourceCodeReference20 from "@carbon/icons-vue/es/repo--source-code/20";
import CodeReference20 from "@carbon/icons-vue/es/code--reference/20";
import TrashCan16 from "@carbon/icons-vue/es/trash-can/16";
import Chip16 from "@carbon/icons-vue/es/chip/16";
import Calendar16 from "@carbon/icons-vue/es/calendar/16";
import Timer16 from "@carbon/icons-vue/es/timer/16";
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";
import DataCheck16 from "@carbon/icons-vue/es/data--check/16";
import Terminal16 from "@carbon/icons-vue/es/terminal/16";
import Tools16 from "@carbon/icons-vue/es/tools/16";
import Earth16 from "@carbon/icons-vue/es/earth/16";
import WarningAlt16 from "@carbon/icons-vue/es/warning--alt/16";
import PlayFilledAlt16 from "@carbon/icons-vue/es/play--filled--alt/16";
import Document16 from "@carbon/icons-vue/es/document/16";

// Stores
import { useBuildsStore } from "@/stores/builds";
import { useRootStore } from "@/stores/root";
import { useTasksStore } from "@/stores/tasks";
import { useOutputsStore } from "@/stores/outputs";
import { CoreSpecs } from "@/types/api/cores";
import { Repository } from "@/types/api/repositories";
import { SourceCode } from "@/types/api/sourceCode";
import { useCoresStore } from "@/stores/cores";

// Global variables
const AsTimeStamp_19900101 = 631152000;

type ComponentData = {
	activeTask: undefined | Partial<Task>;
	showBuildBar: boolean;
	taskAccessState: RemoteAccessState;
	outputsDialog: boolean;
	removeBuildDialog: boolean;
	showRunArgumentsDialog: boolean;
	showMountDataSourcesDialog: boolean;
	taskEventBusSub: (() => void) | null;
	runArgumentsBuffer: string;
	dataSourcesBuffer: DataSource[];
};

export default defineComponent({
	name: "BuildCard",
	components: {
		TooltipButton,
		CopyToClipboardButton,
		SourceReference,
		ActiveCoreTooltip,
		BuildBar,
		ExecutionResultsCard,
		BuildRemoveDialog,
		RunArgumentsDialog,
		MountDataSourcesDialog,
		RepoSourceCodeReference20,
		CodeReference20,
		TrashCan16,
		Chip16,
		Calendar16,
		Timer16,
		ArrowRight16,
		DataCheck16,
		WarningAlt16,
		Earth16,
		Terminal16,
		Tools16,
		PlayFilledAlt16,
		Document16,
	},
	props: {
		buildID: { type: String, required: true },
		buildData: { type: Object as PropType<Build>, required: true },
	},
	data: (): ComponentData => ({
		activeTask: undefined,
		showBuildBar: false,
		taskAccessState: {
			loading: false,
			error: false,
			message: "",
		},
		outputsDialog: false,
		removeBuildDialog: false,
		showRunArgumentsDialog: false,
		showMountDataSourcesDialog: false,
		taskEventBusSub: null,
		runArgumentsBuffer: "",
		dataSourcesBuffer: [],
	}),
	setup() {
		const buildsStore = useBuildsStore();
		const coresStore = useCoresStore();
		const rootStore = useRootStore();
		const tasksStore = useTasksStore();
		const outputsStore = useOutputsStore();
		const cancel = useCancelWithFeedback();
		provide(CancelFeedbackKey, cancel);

		return {
			buildsStore,
			coresStore,
			rootStore,
			tasksStore,
			outputsStore,
			BuildStatusE,
			TaskStatusE,
			isBuildActive,
			isTaskActive,
			isTaskTerminated,
			util,
			cancel,
		};
	},
	watch: {
		"buildData.Status"(newStatus: BuildStatus) {
			const { wasCancelledByUser } = this.cancel.handleBuildStatusUpdate(this.buildData.BuildID, newStatus);
			const buildCompleted = newStatus === BuildStatusE.Completed && !wasCancelledByUser;
			const buildFailed =
				isBuildTerminated(newStatus) &&
				newStatus !== BuildStatusE.Completed &&
				newStatus !== BuildStatusE.Cancelled;

			if (buildCompleted) {
				// @ts-ignore
				this.$posthog?.capture("build_completed", {
					build_id: this.buildData.BuildID,
					build_type: this.buildData.Application?.Type,
					status: "Completed",
				});
			}
			if (buildFailed) {
				// @ts-ignore
				this.$posthog?.capture("build_failed", {
					build_id: this.buildData.BuildID,
					build_type: this.buildData.Application?.Type,
					status: newStatus,
				});
			}
		},
	},
	methods: {
		async cancelActiveBuild() {
			const buildID = this.buildData?.BuildID;
			await this.cancel.cancelBuild(buildID);
			// @ts-ignore
			this.$posthog?.capture("build_cancelled", {
				build_id: buildID,
				build_type: this.buildData.Application?.Type,
				status: "Cancelled",
			});
		},
		normalizeTimestamp(timestamp: number): number {
			return timestamp > 2000000000 ? timestamp : timestamp * 1000;
		},

		secondsSinceEpochToDateString(timestamp: number) {
			//moment requires timestamp in milliseconds
			return moment(this.normalizeTimestamp(timestamp)).calendar(null, {
				sameDay: "[Today]",
				nextDay: "[Tomorrow]",
				nextWeek: "dddd",
				lastDay: "[Yesterday]",
				lastWeek: "[Last] dddd",
				sameElse: "DD/MM/YYYY",
			});
		},
		async runTask() {
			if (!this.buildData) return;

			this.outputsDialog = false;

			this.taskAccessState = {
				loading: false,
				error: false,
				message: "",
			};
			this.activeTask = undefined;
			this.showBuildBar = true;

			this.activeTask = { Status: TaskStatusE.Pending };

			try {
				const response = await sdk.tasks.createTask(this.buildData.BuildID, {
					Arguments: this.runArgumentsBuffer || "",
					DataSources: this.dataSourcesBuffer || [],
				});

				if (!response?.TaskID) {
					throw new Error("Task ID not returned from API");
				}
				const taskID = response.TaskID;

				// @ts-ignore
				this.$posthog?.capture("task_created_from_build", {
					buildID: this.buildData.BuildID,
					taskID: taskID,
				});

				// Fetch the full task to add it to the store and start polling
				const fullTask = await this.tasksStore.getTaskByIdOrFetch(taskID);
				this.activeTask = fullTask;

				// Add to the active list; the store-driven reconcile + WS keep it updated
				this.tasksStore.addToActiveTaskList(fullTask);

				// Subscribe to task event bus for local updates
				this.subscribeToTaskEventBus();
			} catch (error: any) {
				this.taskAccessState.error = true;
				this.taskAccessState.message =
					error?.response?.data?.Message || "Failed to create task. Please try again.";
				monitoringCaptureError(error, "Run task from build");

				if (this.activeTask) {
					this.activeTask.Status = TaskStatusE.Error;
				}
			}
		},
		async cancelActiveTask() {
			const taskID = this.activeTask?.TaskID;
			await this.cancel.cancelTask(taskID, {
				onSent: () => {
					// @ts-ignore
					this.$posthog?.capture("task_cancelled", {
						task_id: taskID,
						build_id: this.buildData?.BuildID,
						status: "Cancelled",
					});
				},
			});
		},
		openOutputsDialog() {
			this.outputsDialog = true;
		},
		async retryTaskOutput() {
			const taskID = this.activeTask?.TaskID;
			if (!taskID) return;
			const { ok } = await this.outputsStore.retryOutputs({
				taskID,
				buildID: this.buildData?.BuildID,
			});
			if (ok) {
				this.$nextTick(() => {
					this.openOutputsDialog();
				});
			}
		},
		closeOutputsDialog() {
			this.outputsDialog = false;
		},
		openRemoveBuildDialog() {
			this.removeBuildDialog = true;
		},
		closeRemoveBuildDialog() {
			this.removeBuildDialog = false;
		},
		removeBuildSuccess() {
			//@ts-ignore
			this.$posthog?.capture("build_metadata_deleted");
		},
		openRunArgumentsDialog() {
			this.showRunArgumentsDialog = true;
		},
		closeRunArgumentsDialog() {
			this.showRunArgumentsDialog = false;
		},
		setRunArguments(payload: { runArguments: string }) {
			// Stage the run arguments locally for the next task request.
			this.runArgumentsBuffer = payload.runArguments;
			this.closeRunArgumentsDialog();
		},
		openMountDataSourcesDialog() {
			this.showMountDataSourcesDialog = true;
		},
		closeMountDataSourcesDialog() {
			this.showMountDataSourcesDialog = false;
		},
		setMountConfig(mountConfig: LocalMountConfig | null | undefined) {
			if (mountConfig === undefined) {
				// Reset to the build's default data sources
				this.dataSourcesBuffer = this.buildData.DefaultDataSources
					? [...this.buildData.DefaultDataSources]
					: [];
			} else if (mountConfig === null) {
				// Remove all data sources
				this.dataSourcesBuffer = [];
			} else {
				// Set or update the data source — replace the first one, keep the rest.
				const dataSource: DataSource = {
					Object: "DataSource",
					ResourceID: mountConfig.ResourceID,
					ResourceType: mountConfig.ResourceType as DataSource["ResourceType"],
					Location: mountConfig.Location,
				};
				this.dataSourcesBuffer = [dataSource, ...this.dataSourcesBuffer.slice(1)];
			}
			this.closeMountDataSourcesDialog();
		},
		subscribeToTaskEventBus() {
			if (this.taskEventBusSub) {
				this.taskEventBusSub();
			}
			this.taskEventBusSub = this.tasksStore.taskEventBus.on(this.taskEventBusCallback.bind(this));
		},
		async taskEventBusCallback(event: any) {
			const activeTaskID = this.activeTask?.TaskID;

			if (!activeTaskID || event.taskData.TaskID !== activeTaskID) {
				console.log("taskEventBusCallback - ignoring event, IDs do not match");
				return;
			}

			if (!activeTaskID || event.taskData.TaskID !== activeTaskID) {
				return;
			}

			this.activeTask = event.taskData;

			this.cancel.handleTaskStatusUpdate(event.taskData.TaskID, event.taskData.Status);

			// Only handle termination on the 'task-completed' event to avoid double-triggering
			if (event.type === "task-completed") {
				// PostHog: Track task lifecycle
				const taskStatus = event.taskData.Status;
				if (taskStatus === TaskStatusE.Completed) {
					// @ts-ignore
					this.$posthog?.capture("task_completed", {
						task_id: activeTaskID,
						build_id: this.buildData.BuildID,
						status: "Completed",
					});
				} else if (isTaskTerminated(taskStatus) && taskStatus !== TaskStatusE.Cancelled) {
					// @ts-ignore
					this.$posthog?.capture("task_failed", {
						task_id: activeTaskID,
						build_id: this.buildData.BuildID,
						status: taskStatus,
					});
				}

				this.tasksStore.removeFromActiveTaskList(activeTaskID);

				const taskWasCancelled = event.taskData.Status === TaskStatusE.Cancelled;
				if (!taskWasCancelled && !this.outputsDialog && this.activeTask?.TaskID) {
					const { ok } = await this.outputsStore.retryOutputs({
						taskID: this.activeTask.TaskID,
						buildID: this.buildData?.BuildID,
					});
					if (!ok) return;
					this.$nextTick(() => {
						this.openOutputsDialog();
					});
				}
			}
		},
	},
	created() {
		// Seed local editable copies from the build's defaults so we never mutate the prop.
		this.runArgumentsBuffer = this.buildData.DefaultArguments || "";
		this.dataSourcesBuffer = this.buildData.DefaultDataSources ? [...this.buildData.DefaultDataSources] : [];
	},
	mounted() {
		// If there's already an active task related to this build, subscribe to its updates
		if (this.activeTask?.TaskID) {
			this.subscribeToTaskEventBus();
		}
	},
	beforeUnmount() {
		// Cleanup event bus subscription
		if (this.taskEventBusSub) {
			this.taskEventBusSub();
			this.taskEventBusSub = null;
		}
	},
	computed: {
		mergedTaskAccessState(): RemoteAccessState {
			return util.mergeAccessState(
				this.outputsStore.getTaskOutputAccessState(this.activeTask?.TaskID),
				this.taskAccessState,
			);
		},
		statusDotClass(): string {
			const colorMap: Record<string, string> = {
				success: "status-dot--success",
				warning: "status-dot--warning",
				error: "status-dot--error",
				info: "status-dot--informal",
			};
			const color = util.buildStatusColor(this.buildData?.Status);
			return colorMap[color] || "status-dot--informal";
		},
		cardSourceCode(): SourceCode | null {
			const build = this.buildData;
			if (!build) return null;
			if (build.Application?.Type == "SourceCode") {
				return build.Application.SourceCode as SourceCode;
			}
			return null;
		},
		cardRepo(): Repository | null {
			const build = this.buildData;
			if (!build) return null;
			if (build.Application?.Type == "Repository") {
				return build.Application.Repository as Repository;
			}
			return null;
		},
		buildStatusMessageOnError: function (): string | null {
			if (this.buildData && !isBuildFail(this.buildData.Status) && this.buildData.Status !== BuildStatusE.Error) {
				return null;
			}
			const statusTransition =
				this.buildData &&
				this.buildData.StatusTransitions?.filter((st) => st.Status == this.buildData?.Status)[0];

			// Kea-cloud errors are formatted as `ErrXXX: Error message.`, where `ErrXXX` is an enum-like identifier.
			// This code checks if the message contains a colon, splits it, and returns the error message without the prefix.
			if (statusTransition && statusTransition.Message?.includes(":")) {
				const messageParts = statusTransition.Message.split(":");
				return messageParts.slice(1).join(":").trim() || null;
			}
			return statusTransition?.Message ?? null;
		},
		buildDuration(): string {
			const secondsOnly = true;
			const AsTimeStamp_19900101 = 631152000;

			if (!this.buildData || !this.buildData?.StartedAt || !this.buildData?.FinishedAt) {
				return "---";
			}
			const startedAtSeconds =
				this.buildData.StartedAt < 1e11
					? this.buildData.StartedAt
					: Math.floor(this.buildData.StartedAt / 1000);
			const finishedAtSeconds =
				this.buildData.FinishedAt < 1e11
					? this.buildData.FinishedAt
					: Math.floor(this.buildData.FinishedAt / 1000);

			if (startedAtSeconds <= AsTimeStamp_19900101 || finishedAtSeconds <= AsTimeStamp_19900101) {
				return "---";
			}

			const finishTime = moment(util.convertToMilliseconds(this.buildData.FinishedAt));
			const startTime = moment(util.convertToMilliseconds(this.buildData.StartedAt));

			if (finishTime.isValid() && startTime.isValid()) {
				const duration = finishTime.diff(startTime);
				if (duration < 1000) {
					// less than a second
					return secondsOnly ? "< 1s" : `${duration}ms`;
				} else if (duration < 60000) {
					// less than a minute
					return `${Math.floor(duration / 1000)}s` + (secondsOnly ? "" : ` ${duration % 1000}ms`);
				} else {
					// minutes and seconds
					return `${Math.floor(duration / 60000)}m ${Math.floor((duration % 60000) / 1000)}s`;
				}
			} else {
				return "---";
			}
		},
		startTimestamp: function (): string {
			if (!this.buildData?.StartedAt || this.buildData.StartedAt <= AsTimeStamp_19900101) {
				return "---";
			}
			return `${this.secondsSinceEpochToDateString(this.buildData.StartedAt)}, ${moment(
				this.normalizeTimestamp(this.buildData.StartedAt),
			).format("HH:mm:ss")}`;
		},
		createdTimestamp: function (): string {
			if (!this.buildData?.CreatedAt || this.buildData.CreatedAt <= AsTimeStamp_19900101) {
				return "---";
			}
			return `${this.secondsSinceEpochToDateString(this.buildData.CreatedAt)}, ${moment(
				this.normalizeTimestamp(this.buildData.CreatedAt),
			).format("HH:mm:ss")}`;
		},
		endTimestamp: function (): string {
			if (!this.buildData) {
				return "---";
			}
			if (
				moment(this.normalizeTimestamp(this.buildData.StartedAt)).isSame(
					this.normalizeTimestamp(this.buildData.FinishedAt),
					"day",
				)
			) {
				return moment(this.normalizeTimestamp(this.buildData.FinishedAt)).format("HH:mm:ss");
			} else {
				return `${this.secondsSinceEpochToDateString(this.buildData.FinishedAt)}, ${moment(
					this.normalizeTimestamp(this.buildData.FinishedAt),
				).format("HH:mm:ss A")}`;
			}
		},
		projectSourceIcon: function (): string {
			return util.projectSourceIcon(this.buildData?.Application?.Type ?? "");
		},
		buildCoreName(): string {
			const coreSpecs = this.buildData?.BuildCoreSpecs;
			if (!coreSpecs) {
				return "Core";
			}
			const defaultCores = this.coresStore.allDefaultCores;
			const chosenCore = defaultCores.find((dc) => {
				const sameClass = dc.Class === coreSpecs.Class;
				const samePrecision = dc.Precision === coreSpecs.Precision;
				const sameMemory = dc.MemorySize === coreSpecs.MemorySize;
				const sameMicroarch = dc.Microarchitecture === coreSpecs.Microarchitecture;
				const sameCorrelation = dc.CorrelationTracking === coreSpecs.CorrelationTracking;

				return sameClass && samePrecision && sameMemory && sameMicroarch && sameCorrelation;
			});
			if (!chosenCore) {
				return "Custom Core";
			}
			return chosenCore?.Name;
		},
		buildCoreSpec(): CoreSpecs | undefined {
			return this.buildData?.BuildCoreSpecs as CoreSpecs;
		},
		buildConfigMk(): string | undefined {
			return this.cardSourceCode?.ConfigMk;
		},
		currentMountConfig(): Partial<LocalMountConfig> | undefined {
			// Return the first data source as a mount config, or undefined if none exist
			if (this.dataSourcesBuffer.length > 0) {
				const firstDataSource = this.dataSourcesBuffer[0];
				return {
					ResourceID: firstDataSource.ResourceID,
					ResourceType: firstDataSource.ResourceType as any,
					Location: firstDataSource.Location,
				};
			}
			return undefined;
		},
	},
});
</script>

<style lang="scss" scoped>
.truncated {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 150px;
}
.source-icon {
	color: var(--sgnl-default-secondary-icon);
}
.build-card-body {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}
.build-error-message {
	font-size: var(--sgnl-font-size-paragraph-s);
	background: var(--sgnl-alert-error-background-primary);
	border-radius: var(--sgnl-border-radius-6);
	padding: var(--sgnl-space-4) var(--sgnl-space-8);
}
.build-error-icon {
	color: var(--sgnl-alert-error-icon);
}
.build-card-inline-actions {
	display: flex;
	gap: var(--sgnl-space-8);
}

@media (max-width: 599px) {
	.build-card-inline-actions {
		width: 100%;
	}
	.build-card-inline-actions :deep(.v-btn) {
		flex: 1 1 0;
		min-width: 0;
	}
}

.argument-area {
	overflow: auto;
	padding: var(--sgnl-space-4);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	border-radius: var(--sgnl-border-radius-6);
	font-size: var(--sgnl-font-size-caption);
	height: fit-content;
	width: 100%;
}
.status-label {
	text-align: center;
	font-size: var(--sgnl-font-size-caption);
	font-style: normal;
	font-weight: 500;
	line-height: var(--sgnl-font-line-height-caption);
}
.status-dot {
	width: var(--sgnl-space-8);
	height: var(--sgnl-space-8);
	border-radius: var(--sgnl-border-radius-4);
	flex-shrink: 0;
}
.status-dot--success {
	background-color: var(--sgnl-alert-success-background);
}
.status-dot--warning {
	background-color: var(--sgnl-alert-warning-background);
}
.status-dot--error {
	background-color: var(--sgnl-alert-error-background);
}
.status-dot--informal {
	background-color: var(--sgnl-alert-info-background);
}
</style>
